import { useCallback, useEffect } from 'react';
import type { Canvas, FabricObject } from 'fabric';
import { fabricToModel } from '../converters/fabric-to-model';
import { isBackgroundPiece, isLayerObject } from '../../canvas/utils/canvas-pieces';
import { usePieceDispatch } from '../piece-context';
import type { PieceModel } from '../types';
import { applyGuard } from './apply-guard';

interface CanvasToModelSyncOptions {
  width: number;
  height: number;
  type: 'image' | 'video';
  fps?: number;
  getVideo?: (id: string) => HTMLVideoElement | null;
}

/** Syncs canvas changes into the model. Returns `readModel`, a pure read for getModel. */
export const useCanvasToModelSync = (
  canvas: Canvas | null,
  opts: CanvasToModelSyncOptions
): (() => PieceModel | null) => {
  const dispatch = usePieceDispatch();
  const { width, height, type, fps, getVideo } = opts;

  const readModel = useCallback((): PieceModel | null => {
    if (!canvas || applyGuard.isApplying) return null;
    return fabricToModel(canvas, { width, height, type, fps, getVideo });
  }, [canvas, width, height, type, fps, getVideo]);

  useEffect(() => {
    if (!canvas) return;

    const sync = (e?: { target?: FabricObject }) => {
      if (applyGuard.isApplying) return;
      // The model owns the background; skip the other system/temporary objects.
      if (e?.target && !isLayerObject(e.target) && !isBackgroundPiece(e.target)) return;
      queueMicrotask(() => {
        const model = readModel();
        if (model) dispatch({ type: 'syncFromCanvas', model });
      });
    };

    sync();
    canvas.on('object:added', sync);
    canvas.on('object:removed', sync);
    canvas.on('object:modified', sync);

    return () => {
      canvas.off('object:added', sync);
      canvas.off('object:removed', sync);
      canvas.off('object:modified', sync);
    };
  }, [canvas, dispatch, readModel]);

  return readModel;
};
